import { useCommonT } from '../../i18n'
import { formatDateBrowser } from '../../i18n'
import { READY_STATE } from '../../lib/offline/offline.codes'
import { DATE_MEDIUM } from '../../lib/offline/offlineUi.codes'
import { allReady, useOfflineReadiness, type ReadyItemState } from '../../lib/offline/readiness'
import OfflineReview from './OfflineReview'

function dateOf(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isFinite(d.getTime()) ? formatDateBrowser(d, DATE_MEDIUM) : ''
}

function stateText(t: ReturnType<typeof useCommonT>, s: ReadyItemState): string {
  switch (s.state) {
    case READY_STATE.ready: return t('offline.ready.state.ready')
    case READY_STATE.downloading: return t('offline.ready.state.downloading', { percent: s.percent ?? 0 })
    case READY_STATE.stale: return t('offline.ready.state.stale', { date: dateOf(s.since) })
    case READY_STATE.tooLarge: return t('offline.ready.state.too_large', { count: s.lineCount ?? 0, limit: s.limit ?? 0 })
    case READY_STATE.notApplicable: return t('offline.ready.state.not_applicable')
    case READY_STATE.error:
      if (s.code === 'storage_full') return t('offline.ready.state.error_storage_full')
      if (s.code === 'forbidden') return t('offline.ready.state.error_forbidden')
      return t('offline.ready.state.error_server_error')
    default: return t('offline.ready.state.not_started')
  }
}

function itemLabel(t: ReturnType<typeof useCommonT>, item: ReadyItemState['item']): string {
  switch (item) {
    case 'decks': return t('offline.ready.item.decks')
    case 'deck_card_data': return t('offline.ready.item.deck_card_data')
    case 'collection': return t('offline.ready.item.collection')
    case 'binders': return t('offline.ready.item.binders')
    case 'card_database': return t('offline.ready.item.card_database')
    case 'scan_database': return t('offline.ready.item.scan_database')
    default: return t('offline.ready.item.images')
  }
}

/**
 * C6 "Offline ready": every item with ready / downloading N% / stale since
 * <date> / too large / an error code. Rendered in Settings (/account) and
 * from the offline bar.
 */
export default function OfflineReadyPanel({ bare = false }: { bare?: boolean }) {
  const t = useCommonT()
  const items = useOfflineReadiness()
  if (!items) return null
  const green = allReady(items)
  return (
    <div data-testid="offline-ready-panel" className={bare ? 'space-y-3' : 'surface rounded-xl p-5 space-y-3'}>
      {!bare && <h2 className="text-base font-semibold text-gray-100">{t('offline.ready.title')}</h2>}
      <p className="text-sm text-gray-400">{t('offline.ready.intro')}</p>
      <ul className="divide-y divide-white/5">
        {items.map(s => {
          const ok = s.state === READY_STATE.ready
          const na = s.state === READY_STATE.notApplicable
          return (
            <li key={s.item} data-testid={`offline-ready-${s.item}`} data-state={s.state} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="text-gray-200">{itemLabel(t, s.item)}</span>
              <span className={`inline-flex items-center gap-1.5 text-xs ${ok ? 'text-green-400' : na ? 'text-gray-500' : 'text-amber-300'}`}>
                <span aria-hidden="true" className={`w-[7px] h-[7px] rounded-full ${ok ? 'bg-green-500' : na ? 'bg-gray-600' : 'bg-amber-400'}`} />
                {stateText(t, s)}
              </span>
            </li>
          )
        })}
      </ul>
      <p className={`text-xs ${green ? 'text-green-400' : 'text-gray-400'}`}>{green ? t('offline.ready.all_ready') : t('offline.ready.not_all_ready')}</p>
      <OfflineReview />
    </div>
  )
}
